import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../apis/store";
import ModalComponent from "../ModalComponent";
import HyperPayAmountSummary from "./HyperPayAmountSummary";
import HyperPayInlineLoader from "./HyperPayInlineLoader";
import HyperPayModalShell from "./HyperPayModalShell";
import {
  primeWpwlOptionsForCardForm,
  unloadHyperPayWidget,
} from "./hyperPayWidgetLoader";
import { useTranslation } from "react-i18next";
import { clearModalBodyLocks } from "../../utils/clearModalBodyLocks";
import { getGrandPayableTotalFromState } from "../../utils/paymentTotals";
import { useHyperPayCheckoutPrepare } from "./useHyperPayCheckoutPrepare";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Charge this instead of the cart total (wallet top-up enters its own amount). */
  amountOverride?: number;
  /** Where the gateway returns to; defaults to the order result page. */
  resultUrl?: string;
}

const MadaForm = ({ isOpen, setIsOpen, amountOverride, resultUrl }: Props) => {
  const { t } = useTranslation();

  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [integrity, setIntegrity] = useState<string | undefined>();
  const { donationAmount, amount, walletAmount, services, products, discountAmount } =
    useSelector((state: RootState) => state?.global);

  const { hyperPayBaseUrl } = useHyperPayCheckoutPrepare(
    isOpen,
    "entityIdMada",
    (id, hash) => {
      setIntegrity(hash);
      setCheckoutId(id);
    },
    setLoading,
    amountOverride
  );

  useEffect(() => {
    if (!isOpen) {
      unloadHyperPayWidget();
      setCheckoutId(null);
      setLoading(false);
      clearModalBodyLocks();
      return;
    }

    if (!checkoutId || !hyperPayBaseUrl) {
      return;
    }

    setLoading(true);
    unloadHyperPayWidget();
    primeWpwlOptionsForCardForm();
    const script = document.createElement("script");
    script.src = `${hyperPayBaseUrl}/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.async = true;
    if (integrity) {
      script.integrity = integrity;
      script.crossOrigin = "anonymous";
    }
    const timeoutId = window.setTimeout(() => setLoading(false), 12000);
    script.onload = () => {
      window.clearTimeout(timeoutId);
      setLoading(false);
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      setLoading(false);
    };
    document.body.appendChild(script);

    return () => {
      window.clearTimeout(timeoutId);
      unloadHyperPayWidget();
      clearModalBodyLocks();
    };
  }, [checkoutId, integrity, hyperPayBaseUrl, isOpen]);

  const handleClose = () => {
    unloadHyperPayWidget();
    setCheckoutId(null);
    setLoading(false);
    clearModalBodyLocks();
    setIsOpen(false);
  };

  const totalAmount =
    amountOverride != null && amountOverride > 0
      ? amountOverride
      : getGrandPayableTotalFromState(
          amount,
          donationAmount,
          walletAmount,
          services,
          products,
          discountAmount
        );

  const showBootLoader = Boolean(isOpen && !checkoutId);
  const showPaymentWidget = Boolean(isOpen && checkoutId);

  return (
    <ModalComponent
      open={isOpen}
      onClose={handleClose}
      title={null}
      closeOnOutsideClick={true}
      rootClassName="ac-modal-hyperpay-overlay"
      className="ac-modal-hyperpay"
      centered
      width="min(100%, 56rem)"
      destroyOnClose
      dialogAriaLabelledBy="ac-hyperpay-modal-title"
    >
      {!isOpen ? null : (
        <HyperPayModalShell
          title={t("payment.hyperpay.title_mada")}
          trustLine={t("payment.hyperpay.secure_line")}
          variant="mada"
        >
          {showBootLoader ? (
            <>
              <HyperPayAmountSummary
                label={t("result.amount")}
                totalNumeric={totalAmount}
              />
              <div className="ac-hyperpay-boot-loader">
                <HyperPayInlineLoader
                  message={t("payment.hyperpay.preparing_session")}
                />
              </div>
            </>
          ) : showPaymentWidget ? (
            <>
              <HyperPayAmountSummary
                label={t("result.amount")}
                totalNumeric={totalAmount}
              />
              <div className="ac-hyperpay-form-block">
                <p className="ac-hyperpay-form-block__eyebrow">
                  {t("payment.hyperpay.form_heading")}
                </p>
                <div
                  className="ac-hyperpay-widget ac-hyperpay-widget--host"
                  dir="ltr"
                >
                  {loading && (
                    <div
                      className="ac-hyperpay-widget__overlay"
                      aria-busy="true"
                      aria-live="polite"
                    >
                      <HyperPayInlineLoader
                        message={t("payment.hyperpay.loading_widget")}
                      />
                    </div>
                  )}
                  <form
                    key={checkoutId as string}
                    action={
                      resultUrl ??
                      window.location.origin + "/result-mada?type=hyperpay"
                    }
                    className="paymentWidgets"
                    data-brands="MADA"
                  />
                </div>
              </div>
            </>
          ) : null}
        </HyperPayModalShell>
      )}
    </ModalComponent>
  );
};

export default MadaForm;
